<template>
  <ui-center-modal v-show="show"
                   class="template-detail"
                   @close="closeModal">
    <div class="calculate-wrap">
      <div class="form-group">
        <h4 class="title">종량제 계산기</h4>
        <div class="form-item used-pv">
          <ui-input :type="'text'"
                    v-model="formattingPV"
                    @input="formatPv"
                    @enter="payAsYouGoCalculate"
                    placeholder="사용 PV를 입력해 주세요.">
            사용 PV
          </ui-input>
          <ui-button :color="'blue'"
                     :disabled="!usedPv"
                     @click="payAsYouGoCalculate">
            확인
          </ui-button>
        </div>
        <div class="form-item">
          <ui-input :type="'apply'"
                    readonly
                    :value="settlePrice ? `${settlePrice}원` : '0원'">
            정산 금액
          </ui-input>
          <span class="guide-text">
            <i class="icon icon--info"></i>
            계산 결과에서 1,000 미만 금액은 절사 처리됩니다.
          </span>
        </div>
      </div>
      <div class="range-table">
        <table>
          <colgroup>
            <col/>
            <col style="width: 17%"/>
            <col style="width: 40%"/>
          </colgroup>
          <thead>
          <tr>
            <th>사용량 구간</th>
            <th>단가</th>
            <th>비용</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, idx) in PLAN_POST_PAID" :key="idx">
            <td>
              {{ item.max === Infinity
              ? `${item.min.toLocaleString()} ~`
              : `${item.min.toLocaleString()} ~ ${item.max.toLocaleString()}` }}
            </td>
            <td>
              {{ item.price }} 원
            </td>
            <td>
              {{ isRangePrice ? getTierCost(item).toLocaleString() : '0' }} 원
            </td>
          </tr>
          <tr class="total-price">
            <td>
              계산 결과
            </td>
            <td >
            </td>
            <td >
              {{ rangeTotPrice ? rangeTotPrice : '0'}} 원
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </ui-center-modal>
</template>

<script>

import UiCenterModal from "@/components/_ui/UiCenterModal.vue";
import UiInput from "@/components/_ui/UiInput.vue";
import UiButton from "@/components/_ui/UiButton.vue";
import {PLAN_POST_PAID} from "@/utils/constants";

export default {
  name: "PostpaidCalculator",
  components: {
    UiButton,
    UiInput,
    UiCenterModal
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      usedPv: 0,
      settlePrice: '',
      formattingPV: '',
      rangeTotPrice: '',
      isRangePrice: false, 
      PLAN_POST_PAID
    }
  },
  watch: {
    usedPv: {
      handler: function (newValue, oldValue) {
        if(oldValue && newValue !== oldValue) {
          this.clearData()
        }
      }
    }
  },
  methods: {
    clearData () {
      this.settlePrice = ''
      this.rangeTotPrice = ''
      this.isRangePrice = false
    },
    initData () {
      this.usedPv =  0
      this.settlePrice =  ''
      this.formattingPV =  ''
      this.rangeTotPrice =  ''
      this.isRangePrice =  false
    },
    // 사용량 구간별 금액
    getTierCost (range) {
      const pv = this.usedPv || 0
      const from = range.min
      const to = Math.min(pv, range.max)

      if (pv > from) {
        const usage = to - from + 1
        return Math.round(usage * range.price)
      }
      return 0
    },
    closeModal () {
      this.initData()
      this.$emit('close')
    },
    // input 숫자만 + 세자리수 콤마 
    formatPv (pv) {
      if(!pv) return this.clearData()
      const onlyNumber = pv.replace(/[^0-9]/g, '');
      this.usedPv = Number(onlyNumber)

      this.formattingPV = onlyNumber ? Number(onlyNumber).toLocaleString() : '';
    },
    // 확인 클릭시 정산금액, 구간별 계산 결과 계산
    payAsYouGoCalculate () {
      this.isRangePrice = true
      const result = this.calculatePostpaid(this.usedPv)
      this.settlePrice = result.rounded.toLocaleString()
      this.rangeTotPrice = result.raw.toLocaleString()
    },
    // 종량제 계산 로직
    calculatePostpaid (pv) {
      let totalRaw = 0

      for (const range of PLAN_POST_PAID) {
        const from = range.min
        const to = Math.min(pv, range.max)

        if (pv > from) {
          const usage = to - from + 1
          const cost = Math.round(usage * range.price)
          totalRaw += cost
        }

        if (pv <= range.max) break
      }

      // 총합 반올림 + 천원 미만 절사
      const totalRounded = Math.floor(totalRaw / 1000) * 1000

      return {
        raw: Math.round(totalRaw),   // 실제 계산된 총 금액
        rounded: totalRounded        // 절사된 청구 금액
      }
    }
  }
}
</script>


<style scoped lang="scss">
.calculate-wrap {
  padding: 32px;
  .form-group {
    .title {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 32px;
    }
    
    .form-item {
      position: relative;
    }

    .used-pv {
      display: flex;
      
      .ui-input {
        flex: 1;
      }
    }
    
    .guide-text {
      position: absolute;
      bottom: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      color: #222222;
      font: 500 13px/13px 'Roboto, "Noto Sans KR", "Noto Sans JP", sans-serif'
    }
  }
  
  .range-table {
    margin-top: 60px;
    width: 100%;
    background-color: #F0F0F0;
    border-radius: 10px;
    
    table {
      width: calc(100% - 32px);
      margin: 0 auto;
      text-align: left;
    }
    
    thead {
      position: relative;
      
      &:before {
        content: '';
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: calc(100% + 32px);
        height: 100%;
        border-radius: 10px;
        overflow: hidden;
        background: #444;
      }
      
      th {
        position: relative;
        color: #fff;
        padding: 10px;
        font: 700 14px/100% 'Roboto, "Noto Sans KR", "Noto Sans JP", sans-serif'
      }
    }
    
    tbody {
      tr {
        &:first-child {
          td {
            padding-top: 10px;  
          }
        }
        
        &.total-price {
          td {
            padding-block: 10px 17px;
            border-bottom: none;
            color: #EC0047;
            font: 700 16px/140% 'Roboto, "Noto Sans KR", "Noto Sans JP", sans-serif';
          }
        }
      }
      td {
        border-bottom: 1px solid #727272;
        padding: 6px 12px;
        color: #797979;
        font: 500 13px/140% 'Roboto, "Noto Sans KR", "Noto Sans JP", sans-serif';
        
        &:last-child {
          color: #222;
        }
      }
    }
  }
}
</style>